import React, { useCallback, useContext } from 'react';
import _ from 'lodash';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import { MLDriverVehicleUtil } from 'ml-portals-util-react';
import { MLTagManagerService, MLModalService } from 'ml-portals-services-react'
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { convertToSentenceCase } from 'ml-portals-util-js';
import PropTypes from 'prop-types';
import appConfig from 'app-config';
import DriverTableComponent from '../../../gw-capability-quoteandbind-common-react/components/DriverTableComponent/DriverTableComponent';
import metadata from './QuotePageDriverTablesComponent.metadata.json5';
import messages from './QuotePageDriverTablesComponent.messages';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { platformMessages } from 'ml-components-platform-react';

const PREFILL_STEP = '/prefill';
const ADDTL_INFO_PREQUOTE_STEP = '/additional-information';
const DRIVERS_STEP = '/drivers';
const ACTION_ADD_DRIVER = 'add';
const ACTION_REMOVE_DRIVER = 'remove';
const ACTION_EDIT_DRIVER = 'edit';

function QuotePageDriverTablesComponent(props) {
    const {
        submissionVM,
        isAK7DayPolicy,
        navigateToStep,
        isGateway
    } = props;

    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        getRatedDriverType,
        getExcludedDriverType,
        getHouseholdDriverType,
        isDriverHousehold
    } = MLDriverVehicleUtil();
    const ratedDriverType = getRatedDriverType();
    const excludedDriverType = getExcludedDriverType();
    const householdDriverType = getHouseholdDriverType();
    const isExclusionAllowed = _.get(submissionVM, 'baseData.canExcludeDrivers_ML.value', true);
    const allDrivers = _.get(submissionVM, 'lobData.personalAuto.coverables.drivers.value', []);
    const ratedDrivers = allDrivers.filter((d) => d.type_ML === ratedDriverType);
    const householdMembers = allDrivers.filter((d) => isDriverHousehold(d));
    const excludedDrivers = allDrivers.filter((d) => d.type_ML === excludedDriverType);
    const hideEditLinks = isAK7DayPolicy();
    const driverTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('DriverType_ML');
    const statusTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('DriverStatusType_ML');
    const rated = driverTypeList.getCode('rated').code;
    const insure = statusTypeList.getCode('insure_driver').code;
    const { authHeader } = useAuthentication();
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const isQnB = appConfig.portalCode === 'QB';

        const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService();

    const onAddDriver = useCallback((type) => {
        let stepName;
        if(type !== ratedDriverType) {
            stepName = ADDTL_INFO_PREQUOTE_STEP;
        } else if (isGateway) {
            stepName = DRIVERS_STEP;
        } else {
            stepName = PREFILL_STEP;
        }
        const data = {
            action: ACTION_ADD_DRIVER,
            type
        };
        navigateToStep(stepName, data);
    }, [isGateway, navigateToStep, ratedDriverType]);

    const onRemoveDriver = useCallback((driverToRemove, type) => {
        let stepName;
        if(type !== ratedDriverType) {
            stepName = ADDTL_INFO_PREQUOTE_STEP;
        } else if (isGateway) {
            stepName = DRIVERS_STEP;
        } else {
            stepName = PREFILL_STEP;
        }
        const data = {
            action: ACTION_REMOVE_DRIVER,
            model: driverToRemove,
            type
        };
        
        navigateToStep(stepName, data);
    }, [isGateway, navigateToStep, ratedDriverType]);

    const onEditDriver = useCallback((driverToEdit, type) => {
        MLTagManagerService.sendEvent('linkClick', {linkName: 'PolicyChange_editDriver'});
        const stepName = type === ratedDriverType ? DRIVERS_STEP : ADDTL_INFO_PREQUOTE_STEP;
        const data = {
            action: ACTION_EDIT_DRIVER,
            model: driverToEdit,
            type
        };
        navigateToStep(stepName, data);
    }, [navigateToStep, ratedDriverType]);

    const insureDriver = useCallback((driverToInsure) => {
        const driversVm = _.get(submissionVM, 'lobData.personalAuto.coverables.drivers');
        const tmp = driversVm.findFirstElement(driverToInsure);
        tmp.status_ML.value = insure;
        tmp.type_ML.value = rated;
        /*
        US988730: Marital status rating error
        If a driver is removed during prefill then marital status is not being set and defaulting to null.
        This is causing a rating error after quote when the driver is being converted to an insured driver.
        Below check will navigate user to driver screen and not cause a rating exception.
        */
        if (tmp.MaritalStatus_ML === undefined || tmp.MaritalStatus_ML === null) {
          const data = {
            action: ACTION_EDIT_DRIVER,
            model: tmp,
            type: rated,
          };
          navigateToStep(DRIVERS_STEP, data);
        }
        const promise = LoadSaveService.updateQuotedSubmission(submissionVM.value, authHeader);
        showProgressDialog(platformMessages.loading, promise, true);

        promise.then(() => {
            const data = {
                action: ACTION_EDIT_DRIVER,
                model: tmp,
                type: rated
            };
            navigateToStep(DRIVERS_STEP, data);
        });
    }, [insure, navigateToStep, rated, submissionVM]);


    const resolvers = {
        resolveComponentMap: {
            driverTableComponent: DriverTableComponent
        }
    };

    const overrides = {
        ratedDriverTable: {
            list: ratedDrivers,
            hideAdd: hideEditLinks,
            hideEdit: hideEditLinks,
            hideRemove: hideEditLinks || (isQnB && ratedDrivers.length === 1),
            title: translator(messages.drivers),
            onAdd: onAddDriver,
            onEdit: onEditDriver,
            onRemove: onRemoveDriver,
            addLabel: isQnB ? convertToSentenceCase(translator(messages.addDriver)) : translator(messages.addDriver),
            driverType: ratedDriverType,
            isGateway,
            showEndHr: !isGateway,
            showPFM: !isModernProduct
        },
        excludedDriverTable: {
            visible: isExclusionAllowed,
            list: excludedDrivers,
            hideAdd: hideEditLinks,
            hideEdit: hideEditLinks,
            hideRemove: hideEditLinks || (isQnB && excludedDrivers.length === 1),
            hideInsure: false,
            title: translator(messages.excludedDrivers),
            onAdd: onAddDriver,
            onEdit: onEditDriver,
            onRemove: onRemoveDriver,
            onInsure: insureDriver,
            addLabel: isQnB ? convertToSentenceCase(translator(messages.addExclDriver)) : translator(messages.addExclDriver),
            driverType: excludedDriverType,
            subtitle: translator(messages.excludedDriversSubtitle),
            helpTitle: translator(messages.exclDriverHelpTitle),
            helpDescription: translator(messages.exclDriverHelpDesc),
            isGateway,
            showEndHr: !isGateway
        },
        householdMemberDriverTable: {
            list: householdMembers,
            hideAdd: hideEditLinks,
            hideEdit: hideEditLinks,
            hideRemove: hideEditLinks || (isQnB && householdMembers.length === 1),
            hideInsure: false,
            title:  submissionVM.isModernProduct_Ext?.value ? translator(messages.nonDrivers) : translator(messages.householdMembers),
            onAdd: onAddDriver,
            onEdit: onEditDriver,
            onRemove: onRemoveDriver,
            onInsure: insureDriver,
            addLabel: submissionVM.isModernProduct_Ext?.value 
                ? isQnB ? convertToSentenceCase(translator(messages.addNonDriver)) : translator(messages.addNonDriver)
                : isQnB ? convertToSentenceCase(translator(messages.addHouseholdMembers)) : translator(messages.addHouseholdMembers),
            driverType: householdDriverType,
            subtitle: submissionVM.isModernProduct_Ext?.value ? translator(messages.nonDriversSubtitle) : translator(messages.householdMembersSubtitle),
            showEndHr: false,
            isGateway
        }
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

QuotePageDriverTablesComponent.propTypes = {
    submissionVM: PropTypes.shape({}).isRequired,
    navigateToStep: PropTypes.func.isRequired,
    isAK7DayPolicy: PropTypes.func.isRequired,
    isGateway: PropTypes.bool
};

QuotePageDriverTablesComponent.defaultProps = {
    isGateway: false
};

export default QuotePageDriverTablesComponent;
